import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { MdQrCode2, MdSearch, MdSelectAll, MdClear, MdPictureAsPdf, MdRefresh, MdFilterList, MdEmail, MdSend, MdHistory, MdExpandMore, MdExpandLess } from 'react-icons/md';
import { userApi, departmentApi, locationApi } from '../../../api/adminapi';
import { generateUserCardsPDF } from '../../../utils/pdfUtils';
import './BulkService.css';

function BulkService({ role }) {
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState('');

  // Filter states
  const [generateFilters, setGenerateFilters] = useState({ department: '', location: '' });
  const [searchQuery, setSearchQuery] = useState('');
  const [filterDepartment, setFilterDepartment] = useState('');
  const [filterLocation, setFilterLocation] = useState('');
  const [qrStatusFilter, setQrStatusFilter] = useState('');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const PAGE_SIZE = 50;

  // Selection state
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Dropdown options
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [locationOptions, setLocationOptions] = useState([]);
  const [loadingDepartments, setLoadingDepartments] = useState(true);
  const [loadingLocations, setLoadingLocations] = useState(true);

  // Success popup
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [newlyGeneratedIds, setNewlyGeneratedIds] = useState([]);

  // Email states
  const [emailSending, setEmailSending] = useState({});
  const [bulkEmailSending, setBulkEmailSending] = useState(false);
  const [emailProgress, setEmailProgress] = useState(null);
  const [showEmailProgressModal, setShowEmailProgressModal] = useState(false);
  const pollingRef = useRef(null);

  // Batch history
  const [batchHistory, setBatchHistory] = useState([]);
  const [showBatchHistory, setShowBatchHistory] = useState(false);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, []);

  // Load batch history
  const loadBatchHistory = useCallback(async () => {
    try {
      const response = await userApi.getEmailBatches();
      const batches = response?.data || response || [];
      setBatchHistory(Array.isArray(batches) ? batches : []);
    } catch (err) {
      console.error('Failed to load batch history:', err);
    }
  }, []);

  // Poll email progress (defined before useEffect that uses it)
  const pollEmailProgress = useCallback((batchId) => {
    if (pollingRef.current) clearInterval(pollingRef.current);

    pollingRef.current = setInterval(async () => {
      try {
        const response = await userApi.getEmailBatchStatus(batchId);
        const status = response?.data || response;
        setEmailProgress(status);

        if (status.status === 'COMPLETED' || status.status === 'FAILED') {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
          setBulkEmailSending(false);
          localStorage.removeItem('qr_email_batch_id');
          loadBatchHistory();
        }
      } catch (err) {
        console.error('Polling error:', err);
        clearInterval(pollingRef.current);
        pollingRef.current = null;
        setBulkEmailSending(false);
        localStorage.removeItem('qr_email_batch_id');
      }
    }, 2000);
  }, [loadBatchHistory]);

  // Restore active batch on mount
  useEffect(() => {
    const savedBatchId = localStorage.getItem('qr_email_batch_id');
    if (savedBatchId) {
      // Try to restore polling for this batch
      (async () => {
        try {
          const response = await userApi.getEmailBatchStatus(savedBatchId);
          const status = response?.data || response;
          if (status && (status.status === 'QUEUED' || status.status === 'IN_PROGRESS')) {
            setEmailProgress(status);
            setShowEmailProgressModal(true);
            setBulkEmailSending(true);
            pollEmailProgress(savedBatchId);
          } else if (status && status.status === 'COMPLETED') {
            setEmailProgress(status);
            setShowEmailProgressModal(true);
            localStorage.removeItem('qr_email_batch_id');
          } else {
            localStorage.removeItem('qr_email_batch_id');
          }
        } catch {
          localStorage.removeItem('qr_email_batch_id');
        }
      })();
    }
    loadBatchHistory();
  }, [pollEmailProgress, loadBatchHistory]);

  // Fetch departments and locations on mount
  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        setLoadingDepartments(true);
        const response = await departmentApi.getActiveCodes();
        const departments = Array.isArray(response) ? response :
                           (response?.data && Array.isArray(response.data) ? response.data : []);
        setDepartmentOptions(departments);
      } catch (error) {
        console.error('Error fetching departments:', error);
        setDepartmentOptions([]);
      } finally {
        setLoadingDepartments(false);
      }
    };

    const fetchLocations = async () => {
      try {
        setLoadingLocations(true);
        const response = await locationApi.getAllActiveLocationNames();
        const locations = Array.isArray(response) ? response :
                         (response?.data && Array.isArray(response.data) ? response.data : []);
        setLocationOptions(locations);
      } catch (error) {
        console.error('Error fetching locations:', error);
        setLocationOptions([]);
      } finally {
        setLoadingLocations(false);
      }
    };

    fetchDepartments();
    fetchLocations();
  }, []);

  // Load first page on mount
  useEffect(() => { loadUsersWithQR(0); }, []);

  // Debounced server-side search — fires 400ms after user stops typing
  useEffect(() => {
    const timer = setTimeout(() => {
      loadUsersWithQR(0, searchQuery);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const loadUsersWithQR = async (page = 0, search = '') => {
    try {
      setIsLoading(true);
      setError('');
      setSelectedIds(new Set());

      const data = await userApi.getUsersQRStatus(page, PAGE_SIZE, search);
      const payload = data?.data || data;

      if (payload?.users && Array.isArray(payload.users)) {
        setUsers(payload.users);
        setCurrentPage(payload.currentPage ?? page);
        setTotalPages(payload.totalPages ?? 1);
        setTotalElements(payload.totalElements ?? payload.users.length);
      } else if (Array.isArray(payload)) {
        setUsers(payload);
        setTotalPages(1);
        setTotalElements(payload.length);
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to fetch users');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenerateQR = async () => {
    if (!generateFilters.department || !generateFilters.location) {
      setError('Please select both department and location to generate QR codes');
      return;
    }

    try {
      setIsGenerating(true);
      setError('');

      const response = await userApi.generateBulkQR(generateFilters.department, generateFilters.location);

      const generatedUsers = Array.isArray(response)
        ? response
        : (Array.isArray(response?.data) ? response.data
          : (Array.isArray(response?.content) ? response.content
            : (Array.isArray(response?.users) ? response.users : [])));

      // Capture newly generated user IDs
      const newIds = generatedUsers.map(u => u.id).filter(Boolean);
      setNewlyGeneratedIds(newIds);

      const defaultMsg = `QR codes generated successfully for ${generateFilters.department} - ${generateFilters.location}. (${generatedUsers.length} users)`;
      setSuccessMessage(response?.message || defaultMsg);
      setShowSuccessPopup(true);

      // Refresh the list and auto-select newly generated users
      await loadUsersWithQR();
      if (newIds.length > 0) {
        setSelectedIds(new Set(newIds));
      }

    } catch (err) {
      console.error('Error:', err);
      setError(err.message || 'Failed to generate bulk QR codes');
    } finally {
      setIsGenerating(false);
    }
  };

  // Filter users for display
  const filteredUsers = useMemo(() => {
    const query = (searchQuery || '').toLowerCase().trim();
    const dept = (filterDepartment || '').toLowerCase();
    const loc = (filterLocation || '').toLowerCase();

    return (users || []).filter(u => {
      const matchesDept = !dept || (u.department || '').toLowerCase() === dept;
      const matchesLoc = !loc || (u.location || '').toLowerCase() === loc;

      // QR status filter
      let matchesQR = true;
      if (qrStatusFilter === 'generated') {
        matchesQR = !!u.qrGenerated;
      } else if (qrStatusFilter === 'no_qr') {
        matchesQR = !u.qrGenerated;
      }

      if (!query) return matchesDept && matchesLoc && matchesQR;

      const haystack = [
        u.firstName, u.secondName, u.email, u.empId,
        u.department, u.location, u.phone
      ].map(x => (x || '').toString().toLowerCase()).join(' ');

      return matchesDept && matchesLoc && matchesQR && haystack.includes(query);
    });
  }, [users, searchQuery, filterDepartment, filterLocation, qrStatusFilter]);

  // Selection helpers
  const getUserKey = (u) => u?.id || u?.empId || u?.email;

  const toggleSelect = (user) => {
    const id = getUserKey(user);
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    const all = new Set(filteredUsers.map(u => getUserKey(u)));
    setSelectedIds(all);
  };

  const clearSelection = () => setSelectedIds(new Set());

  const handleDownloadPDF = async () => {
    const selectedUsers = filteredUsers.filter(user => selectedIds.has(getUserKey(user)));
    if (selectedUsers.length === 0) {
      alert('Please select at least one user to download as PDF.');
      return;
    }

    try {
      const filename = `qr-cards-${new Date().toISOString().split('T')[0]}.pdf`;
      await generateUserCardsPDF(selectedUsers, filename);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    }
  };

  const handleCloseSuccessPopup = () => {
    setShowSuccessPopup(false);
    setSuccessMessage('');
    setNewlyGeneratedIds([]);
  };

  const handleEmailNewlyGenerated = () => {
    setShowSuccessPopup(false);
    setSuccessMessage('');
    // selectedIds are already set to newly generated users
    handleSendBulkEmail();
    setNewlyGeneratedIds([]);
  };

  // --- Email Handlers ---

  const handleSendSingleEmail = async (user) => {
    const userId = getUserKey(user);
    setEmailSending(prev => ({ ...prev, [userId]: true }));
    setError('');
    try {
      const response = await userApi.sendQRCodeEmail(userId);
      setSuccessMessage(response?.message || `QR code email sent to ${user.email}`);
      setShowSuccessPopup(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send email');
    } finally {
      setEmailSending(prev => ({ ...prev, [userId]: false }));
    }
  };

  const handleSendBulkEmail = async () => {
    const selectedUserIds = Array.from(selectedIds);
    if (selectedUserIds.length === 0) {
      setError('Please select at least one user to email QR codes');
      return;
    }

    setBulkEmailSending(true);
    setShowEmailProgressModal(true);
    setEmailProgress({ totalCount: selectedUserIds.length, sentCount: 0, failedCount: 0, status: 'QUEUED', failedEmails: [] });
    setError('');

    try {
      const response = await userApi.sendBulkQRCodeEmail(selectedUserIds);
      const batchId = response?.data?.batchId;
      if (batchId) {
        localStorage.setItem('qr_email_batch_id', batchId);
        pollEmailProgress(batchId);
      } else {
        setBulkEmailSending(false);
        setEmailProgress(prev => ({ ...prev, status: 'COMPLETED' }));
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to start bulk email');
      setBulkEmailSending(false);
      setShowEmailProgressModal(false);
    }
  };

  const handleCloseEmailProgress = () => {
    setShowEmailProgressModal(false);
    setEmailProgress(null);
    localStorage.removeItem('qr_email_batch_id');
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    loadBatchHistory();
  };

  // Derive unique departments and locations from current users for table filters
  const tableDepartments = useMemo(() => {
    const depts = Array.from(new Set((users || []).map(u => u.department).filter(Boolean)));
    return depts.sort();
  }, [users]);

  const tableLocations = useMemo(() => {
    const locs = Array.from(new Set((users || []).map(u => u.location).filter(Boolean)));
    return locs.sort();
  }, [users]);

  return (
    <div className="bulk-service-page">
      {/* Page Header */}
      <div className="page-header-compact">
        <div className="header-left-compact">
          <h2 className="page-title-compact">
            <MdQrCode2 className="title-icon" />
            Bulk QR Generation
          </h2>
          <span className="header-subtitle">Generate and manage QR codes for users</span>
        </div>
      </div>

      {/* Generate QR Section */}
      <div className="generate-section">
        <div className="section-header">
          <MdFilterList className="section-icon" />
          <h3>Generate QR Codes</h3>
        </div>
        <div className="generate-controls">
          <select
            className="select-input"
            value={generateFilters.department}
            onChange={(e) => setGenerateFilters(prev => ({ ...prev, department: e.target.value }))}
            disabled={loadingDepartments || isGenerating}
          >
            <option value="">
              {loadingDepartments ? 'Loading...' : 'Select Department'}
            </option>
            {departmentOptions.map(dept => (
              <option key={dept} value={dept}>{dept}</option>
            ))}
          </select>

          <select
            className="select-input"
            value={generateFilters.location}
            onChange={(e) => setGenerateFilters(prev => ({ ...prev, location: e.target.value }))}
            disabled={loadingLocations || isGenerating}
          >
            <option value="">
              {loadingLocations ? 'Loading...' : 'Select Location'}
            </option>
            {locationOptions.map(loc => (
              <option key={loc} value={loc}>{loc}</option>
            ))}
          </select>

          <button
            className="btn-generate"
            onClick={handleGenerateQR}
            disabled={isGenerating || !generateFilters.department || !generateFilters.location}
          >
            <MdQrCode2 />
            {isGenerating ? 'Generating...' : 'Generate QR Codes'}
          </button>

          <button
            className="btn-refresh"
            onClick={() => loadUsersWithQR(currentPage, searchQuery)}
            disabled={isLoading}
            title="Refresh list"
          >
            <MdRefresh className={isLoading ? 'spinning' : ''} />
          </button>
        </div>
        {error && <div className="error-alert">{error}</div>}
      </div>

      {/* Users Table Section */}
      <div className="users-section">
        <div className="section-toolbar">
          <div className="toolbar-left">
            <div className="search-box">
              <MdSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search users..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            <select
              className="filter-select"
              value={filterDepartment}
              onChange={(e) => setFilterDepartment(e.target.value)}
            >
              <option value="">All Departments</option>
              {tableDepartments.map(dept => (
                <option key={dept} value={dept}>{dept}</option>
              ))}
            </select>

            <select
              className="filter-select"
              value={filterLocation}
              onChange={(e) => setFilterLocation(e.target.value)}
            >
              <option value="">All Locations</option>
              {tableLocations.map(loc => (
                <option key={loc} value={loc}>{loc}</option>
              ))}
            </select>

            <select
              className="filter-select"
              value={qrStatusFilter}
              onChange={(e) => setQrStatusFilter(e.target.value)}
            >
              <option value="">All QR Status</option>
              <option value="generated">QR Generated</option>
              <option value="no_qr">No QR</option>
            </select>

            <button className="btn-text" onClick={() => { setSearchQuery(''); setFilterDepartment(''); setFilterLocation(''); setQrStatusFilter(''); }}>
              Reset
            </button>
          </div>

          <div className="toolbar-right">
            <span className="user-count">
              {filteredUsers.length} user{filteredUsers.length !== 1 ? 's' : ''}
              {selectedIds.size > 0 && ` (${selectedIds.size} selected)`}
            </span>

            <button className="btn-icon" onClick={selectAll} title="Select all">
              <MdSelectAll />
            </button>

            <button className="btn-icon" onClick={clearSelection} title="Clear selection">
              <MdClear />
            </button>

            <button
              className="btn-email-bulk"
              onClick={handleSendBulkEmail}
              disabled={selectedIds.size === 0 || bulkEmailSending}
            >
              <MdSend />
              {bulkEmailSending ? 'Sending...' : `Email Selected (${selectedIds.size})`}
            </button>

            <button
              className="btn-download"
              onClick={handleDownloadPDF}
              disabled={selectedIds.size === 0}
            >
              <MdPictureAsPdf />
              Download PDF
            </button>
          </div>
        </div>

        {/* Users Table */}
        {isLoading ? (
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Loading users...</p>
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="empty-state">
            <MdQrCode2 className="empty-icon" />
            <p>No users found</p>
            <span>Try adjusting your filters or generate QR codes using the form above</span>
          </div>
        ) : (
          <div className="users-table">
            <div className="table-header">
              <div className="col-checkbox">
                <input
                  type="checkbox"
                  checked={selectedIds.size === filteredUsers.length && filteredUsers.length > 0}
                  onChange={(e) => e.target.checked ? selectAll() : clearSelection()}
                />
              </div>
              <div className="col-emp-id">Employee ID</div>
              <div className="col-name">Name</div>
              <div className="col-department">Department</div>
              <div className="col-location">Location</div>
              <div className="col-qr">QR Code</div>
              <div className="col-actions">Actions</div>
            </div>

            <div className="table-body">
              {filteredUsers.map((user) => {
                const key = getUserKey(user);
                const isSelected = selectedIds.has(key);
                const qrSrc = user?.qrGenerated;
                const isSendingEmail = emailSending[key];

                return (
                  <div
                    key={key}
                    className={`table-row ${isSelected ? 'selected' : ''}`}
                    onClick={() => toggleSelect(user)}
                  >
                    <div className="col-checkbox">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelect(user)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                    <div className="col-emp-id">
                      <span className="emp-id-badge">{user.empId || 'N/A'}</span>
                    </div>
                    <div className="col-name">
                      <div className="user-name">{user.firstName} {user.secondName}</div>
                      <div className="user-email">{user.email}</div>
                    </div>
                    <div className="col-department">
                      <span className="dept-badge">{user.department || 'N/A'}</span>
                    </div>
                    <div className="col-location">
                      <span className="location-badge">{user.location || 'N/A'}</span>
                    </div>
                    <div className="col-qr">
                      {qrSrc ? (
                        <div className="qr-placeholder-small qr-generated">Generated</div>
                      ) : (
                        <div className="qr-placeholder-small">No QR</div>
                      )}
                    </div>
                    <div className="col-actions">
                      <button
                        className="btn-email-single"
                        onClick={(e) => { e.stopPropagation(); handleSendSingleEmail(user); }}
                        disabled={isSendingEmail || !user.qrGenerated}
                        title={user.qrGenerated ? 'Email QR code' : 'No QR code available'}
                      >
                        <MdEmail />
                        {isSendingEmail ? 'Sending...' : 'Email'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', borderTop: '1px solid #e5e7eb', background: '#fff' }}>
            <span style={{ fontSize: '13px', color: '#6b7280' }}>
              Showing {currentPage * PAGE_SIZE + 1}–{Math.min((currentPage + 1) * PAGE_SIZE, totalElements)} of {totalElements} users
            </span>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <button
                onClick={() => loadUsersWithQR(currentPage - 1, searchQuery)}
                disabled={currentPage === 0 || isLoading}
                style={{ padding: '6px 14px', border: '1px solid #d1d5db', borderRadius: '6px', background: currentPage === 0 ? '#f3f4f6' : '#fff', cursor: currentPage === 0 ? 'not-allowed' : 'pointer', fontSize: '13px', color: '#374151' }}
              >
                ← Prev
              </button>
              <span style={{ fontSize: '13px', fontWeight: 600, color: '#374151' }}>
                Page {currentPage + 1} of {totalPages}
              </span>
              <button
                onClick={() => loadUsersWithQR(currentPage + 1, searchQuery)}
                disabled={currentPage >= totalPages - 1 || isLoading}
                style={{ padding: '6px 14px', border: '1px solid #d1d5db', borderRadius: '6px', background: currentPage >= totalPages - 1 ? '#f3f4f6' : '#fff', cursor: currentPage >= totalPages - 1 ? 'not-allowed' : 'pointer', fontSize: '13px', color: '#374151' }}
              >
                Next →
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Success Popup Modal */}
      {showSuccessPopup && (
        <div className="modal-overlay" onClick={handleCloseSuccessPopup}>
          <div className="modal-content success-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Success!</h3>
              <button className="modal-close-btn" onClick={handleCloseSuccessPopup}>×</button>
            </div>
            <div className="modal-body">
              <div className="success-icon">✓</div>
              <p>{successMessage}</p>
              {newlyGeneratedIds.length > 0 && (
                <p style={{ marginTop: '12px', fontSize: '14px', color: '#475569' }}>
                  Would you like to send QR code emails to these {newlyGeneratedIds.length} user{newlyGeneratedIds.length !== 1 ? 's' : ''}?
                </p>
              )}
            </div>
            <div className="modal-footer" style={{ gap: '10px' }}>
              {newlyGeneratedIds.length > 0 && (
                <button className="btn-email-bulk" onClick={handleEmailNewlyGenerated}>
                  <MdSend /> Send QR Emails ({newlyGeneratedIds.length})
                </button>
              )}
              <button className="btn btn-primary" onClick={handleCloseSuccessPopup}>
                {newlyGeneratedIds.length > 0 ? 'Skip' : 'OK'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Email Progress Modal */}
      {showEmailProgressModal && emailProgress && (
        <div className="modal-overlay">
          <div className="modal-content email-progress-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header email-header">
              <h3>Sending QR Code Emails</h3>
              {emailProgress.status === 'COMPLETED' && (
                <button className="modal-close-btn" onClick={handleCloseEmailProgress}>×</button>
              )}
            </div>
            <div className="modal-body">
              <div className="progress-stats">
                <div className="progress-bar-container">
                  <div
                    className="progress-bar-fill"
                    style={{
                      width: `${emailProgress.totalCount > 0
                        ? ((emailProgress.sentCount + emailProgress.failedCount) / emailProgress.totalCount) * 100
                        : 0}%`
                    }}
                  />
                </div>
                <p className="progress-text">
                  Sent {emailProgress.sentCount} of {emailProgress.totalCount}
                  {emailProgress.failedCount > 0 && ` (${emailProgress.failedCount} failed)`}
                </p>
                <p className="progress-status">{emailProgress.status}</p>
              </div>
              {emailProgress.status === 'COMPLETED' && (
                <div className="progress-complete">
                  All emails have been processed.
                </div>
              )}
              {emailProgress.failedEmails && emailProgress.failedEmails.length > 0 && (
                <div className="failed-emails">
                  <strong>Failed to send to:</strong>
                  <ul>
                    {emailProgress.failedEmails.map((email, i) => <li key={i}>{email}</li>)}
                  </ul>
                </div>
              )}
            </div>
            <div className="modal-footer">
              {emailProgress.status === 'COMPLETED' ? (
                <button className="btn btn-primary" onClick={handleCloseEmailProgress}>Close</button>
              ) : (
                <span className="progress-note">Please wait while emails are being sent...</span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Batch History Section */}
      {batchHistory.length > 0 && (
        <div className="batch-history-section">
          <button
            className="batch-history-toggle"
            onClick={() => setShowBatchHistory(!showBatchHistory)}
          >
            <MdHistory className="section-icon" />
            <span>Recent Email Batches ({batchHistory.length})</span>
            {showBatchHistory ? <MdExpandLess /> : <MdExpandMore />}
          </button>

          {showBatchHistory && (
            <div className="batch-history-table">
              <div className="batch-header-row">
                <div className="batch-col-time">Time</div>
                <div className="batch-col-total">Total</div>
                <div className="batch-col-sent">Sent</div>
                <div className="batch-col-failed">Failed</div>
                <div className="batch-col-status">Status</div>
              </div>
              {batchHistory.map((batch) => (
                <div key={batch.batchId} className="batch-row">
                  <div className="batch-col-time">
                    {batch.createdAt ? new Date(batch.createdAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) : 'N/A'}
                  </div>
                  <div className="batch-col-total">{batch.totalCount}</div>
                  <div className="batch-col-sent">{batch.sentCount}</div>
                  <div className="batch-col-failed">
                    {batch.failedCount > 0 ? (
                      <span className="failed-count">{batch.failedCount}</span>
                    ) : '0'}
                  </div>
                  <div className="batch-col-status">
                    <span className={`batch-status-badge batch-status-${(batch.status || '').toLowerCase()}`}>
                      {batch.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default BulkService;
